import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './utils/experienceStore';
import { Plus, Settings, X, ChevronDown } from 'lucide-react';
import VisualProcessingFlowEditor from './VisualProcessingFlowEditor';
import { setFlowEditorOpen } from './utils/state/canvasSlice';
import { updateLegacyFlow } from './utils/state/processingFlowSlice';
import useProcessing, {
    ProcessingFlow,
} from '../Processing/components/hooks/useProcessing';
import { Provider } from 'react-redux';
import createExperienceStore from './utils/experienceStore';

const { store } = createExperienceStore();

interface FlowSelectorProps {
    value?: number | number[] | string | string[];
    onChange: (value: number | number[] | string | string[]) => void;
    multiple?: boolean;
    label?: string;
    placeholder?: string;
    className?: string;
}

interface FlowSelectorContentProps extends FlowSelectorProps {
    // Internal props for the content component
}

function FlowSelectorContent({
    value,
    onChange,
    multiple = false,
    label,
    placeholder = multiple ? 'Select flows...' : 'Select a flow...',
    className = '',
}: FlowSelectorContentProps) {
    const dispatch = useDispatch();
    const {
        processors,
        environments,
        createProcessingFlow,
        updateProcessingFlow,
    } = useProcessing();

    const [availableFlows, setAvailableFlows] = useState<ProcessingFlow[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isCreatingFlow, setIsCreatingFlow] = useState(false);
    const [editingFlowId, setEditingFlowId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);

    // Get legacy flows from Redux state
    const legacyFlows = useSelector(
        (state: RootState) => state.flows.present.legacyFlows,
    );

    // Load available flows
    useEffect(() => {
        const loadFlows = async () => {
            try {
                // For now, we'll create mock flows from legacy flows and processors
                const mockFlows: ProcessingFlow[] = [];

                // Add flows from legacy flows in Redux
                Object.keys(legacyFlows).forEach((flowId, index) => {
                    mockFlows.push({
                        id: parseInt(flowId) || index + 1,
                        galleryId: 1, // This would come from context
                        enabled: true,
                        environmentId: 1,
                        flowDefinition: JSON.stringify(legacyFlows[flowId]),
                    });
                });

                // Add some example flows if none exist
                if (mockFlows.length === 0) {
                    mockFlows.push(
                        {
                            id: 1,
                            galleryId: 1,
                            enabled: true,
                            environmentId: 1,
                            flowDefinition: JSON.stringify([]),
                        },
                        {
                            id: 2,
                            galleryId: 1,
                            enabled: true,
                            environmentId: 1,
                            flowDefinition: JSON.stringify([]),
                        },
                    );
                }

                setAvailableFlows(mockFlows);
            } catch (error) {
                console.error('Error loading flows:', error);
            } finally {
                setLoading(false);
            }
        };

        loadFlows();
    }, [legacyFlows]);

    const handleFlowSelect = (flowId: number) => {
        if (multiple) {
            const currentValues = Array.isArray(value)
                ? (value as number[])
                : [];
            const newValues = currentValues.includes(flowId)
                ? currentValues.filter((id) => id !== flowId)
                : [...currentValues, flowId];
            onChange(newValues);
        } else {
            onChange(flowId);
            setIsDropdownOpen(false);
        }
    };

    const handleCreateFlow = () => {
        setIsCreatingFlow(true);
        setIsDropdownOpen(false);
        dispatch(setFlowEditorOpen(true));
    };

    const handleEditFlow = (flowId: number) => {
        setEditingFlowId(flowId);
        setIsDropdownOpen(false);
        dispatch(setFlowEditorOpen(true));
    };

    const handleFlowSaved = async (nodes: any[], enabled: boolean) => {
        try {
            // Convert visual flow nodes back to legacy format with string arguments
            const flowDefinition = nodes.map((node) => {
                // Ensure all arguments are strings
                const stringArguments: { [key: string]: string } = {};
                if (node.arguments && typeof node.arguments === 'object') {
                    Object.keys(node.arguments).forEach((key) => {
                        stringArguments[key] = String(node.arguments[key]);
                    });
                }

                return {
                    ...node.processor,
                    arguments: stringArguments,
                    saveOutput: node.saveOutput,
                };
            });

            if (editingFlowId) {
                // Update existing flow in Redux state
                dispatch(
                    updateLegacyFlow({
                        id: editingFlowId.toString(),
                        flowDefinition: flowDefinition,
                    }),
                );

                // Also try to update via API (but don't fail if it doesn't work)
                try {
                    const existingFlow = availableFlows.find(
                        (f) => f.id === editingFlowId,
                    );
                    if (existingFlow) {
                        await updateProcessingFlow(
                            editingFlowId,
                            JSON.stringify(flowDefinition),
                            existingFlow.environmentId,
                            enabled,
                        );
                    }
                } catch (apiError) {
                    console.warn(
                        'API update failed, but local state updated:',
                        apiError,
                    );
                }
            } else {
                // For new flows, we need to determine the flow ID
                // Use the next available ID from existing flows
                const existingIds = availableFlows.map((f) => f.id);
                const nextId = Math.max(...existingIds, 0) + 1;
                const flowId = nextId.toString();

                // Update Redux state with new flow
                dispatch(
                    updateLegacyFlow({
                        id: flowId,
                        flowDefinition: flowDefinition,
                    }),
                );

                // Auto-select the newly created flow
                if (multiple) {
                    const currentValues = Array.isArray(value) ? value : [];
                    onChange([...currentValues, nextId] as number[]);
                } else {
                    onChange(nextId);
                }

                // Also try to create via API (but don't fail if it doesn't work)
                try {
                    const newFlow = await createProcessingFlow(
                        1, // galleryId - this should come from context
                        JSON.stringify(flowDefinition),
                        1, // environmentId for local
                        enabled,
                    );
                    console.log('Flow created via API:', newFlow);
                } catch (apiError) {
                    console.warn(
                        'API creation failed, but local state updated:',
                        apiError,
                    );
                }
            }

            // Refresh flows list
            setIsCreatingFlow(false);
            setEditingFlowId(null);
            dispatch(setFlowEditorOpen(false));
        } catch (error) {
            console.error('Error saving flow:', error);
        }
    };

    const getSelectedFlowNames = () => {
        if (!value) return [];

        const selectedIds = Array.isArray(value)
            ? (value as number[])
            : [value as number];
        return selectedIds.map((id) => {
            const flow = availableFlows.find((f) => f.id === id);
            return flow ? `Flow ${flow.id}` : `Flow ${id}`;
        });
    };

    const getDisplayText = () => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
            return placeholder;
        }

        const names = getSelectedFlowNames();
        if (multiple) {
            return names.length > 2
                ? `${names.slice(0, 2).join(', ')} +${names.length - 2} more`
                : names.join(', ');
        }

        return names[0] || placeholder;
    };

    if (isCreatingFlow || editingFlowId) {
        const currentFlow = editingFlowId
            ? availableFlows.find((f) => f.id === editingFlowId)
            : undefined;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                <div
                    className="w-full bg-background"
                    style={{ height: 'calc(100vh - 4rem)', marginTop: '4rem' }}
                >
                    <VisualProcessingFlowEditor
                        flow={currentFlow}
                        processors={processors}
                        environments={environments}
                        onSave={handleFlowSaved}
                        onClose={() => {
                            setIsCreatingFlow(false);
                            setEditingFlowId(null);
                            dispatch(setFlowEditorOpen(false));
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            {label && (
                <label className="block text-sm font-medium text-foreground mb-2">
                    {label}
                </label>
            )}

            <div className="relative pr-2 mr-4 bg-background">
                <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md shadow-sm text-left bg-background hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                    disabled={loading}
                >
                    <span
                        className={`block truncate ${
                            !value ||
                            (Array.isArray(value) && value.length === 0)
                                ? 'text-muted-foreground'
                                : 'text-foreground'
                        }`}
                    >
                        {loading ? 'Loading flows...' : getDisplayText()}
                    </span>
                    <ChevronDown
                        className={`w-4 h-4 text-foreground transition-transform ${
                            isDropdownOpen ? 'rotate-180' : ''
                        }`}
                    />
                </button>

                {isDropdownOpen && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                        {/* Create New Flow Option */}
                        <button
                            type="button"
                            onClick={handleCreateFlow}
                            className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center space-x-2 border-b border-border"
                        >
                            <Plus className="w-4 h-4 text-blue-600" />
                            <span className="text-blue-600 font-medium">
                                Create New Flow
                            </span>
                        </button>

                        {/* Available Flows */}
                        {availableFlows.length === 0 ? (
                            <div className="px-4 py-2 text-muted-foreground text-sm">
                                No flows available
                            </div>
                        ) : (
                            availableFlows.map((flow) => {
                                const isSelected = multiple
                                    ? Array.isArray(value) &&
                                      (value as number[]).includes(flow.id)
                                    : value === flow.id;

                                return (
                                    <div
                                        key={flow.id}
                                        className={`flex items-center justify-between px-4 py-2 hover:bg-muted/50 ${
                                            isSelected ? 'bg-blue-50' : ''
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleFlowSelect(flow.id)
                                            }
                                            className="flex-1 text-left flex items-center space-x-3"
                                        >
                                            {multiple && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}} // Handled by parent click
                                                    className="rounded border-border text-blue-600 focus:ring-blue-500"
                                                />
                                            )}
                                            <div className="flex-1">
                                                <div className="font-medium text-foreground">
                                                    Flow {flow.id}
                                                </div>
                                                <div className="text-sm text-muted-foreground">
                                                    {flow.enabled
                                                        ? 'Enabled'
                                                        : 'Disabled'}{' '}
                                                    •
                                                    {
                                                        JSON.parse(
                                                            flow.flowDefinition ||
                                                                '[]',
                                                        ).length
                                                    }{' '}
                                                    steps
                                                </div>
                                            </div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleEditFlow(flow.id)
                                            }
                                            className="p-1 text-muted-foreground hover:text-muted-foreground rounded"
                                            title="Edit flow"
                                        >
                                            <Settings className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            {/* Selected flows display for multiple selection */}
            {multiple && value && Array.isArray(value) && value.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {(value as number[]).map((flowId) => {
                        return (
                            <span
                                key={flowId}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                            >
                                Flow {flowId}
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newValues = (
                                            value as number[]
                                        ).filter((id) => id !== flowId);
                                        onChange(newValues);
                                    }}
                                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function FlowSelector(props: FlowSelectorProps) {
    return (
        <Provider store={store}>
            <FlowSelectorContent {...props} />
        </Provider>
    );
}
